import mountFarloRangeCalendar from './rangeCalendar';
import { hydrateTicketListing } from './ticketListing';
import {
	initFilterBar,
	getInlineObj,
	updateAllFiltersCount,
	updateClearChip,
	wireCalendarEnable,
	paintChoiceActives,
	markScopeDirty,
	adjustPopoverPosition,
	resolveModalGoto,
	applyOfferDateVisibility
} from './filtersCore';
import { createFilterTracker } from './filterAnalytics';

// Helper to clean backslashes
function cleanBackslashes(value) {
	if (!value) {
		return value;
	}
	let cleaned = value;
	const maxIterations = 20;
	let iterations = 0;
	// Remove excessive double backslashes
	while (cleaned.indexOf('\\\\') !== -1 && iterations < maxIterations) {
		cleaned = cleaned.replace(/\\\\/g, '\\');
		iterations ++;
	}
	// Remove backslashes before quotes
	cleaned = cleaned.replace(/\\'/g, "'").replace(/\\"/g, '"');
	return cleaned;
}

function snapshotPanel(panel) {
	if (!panel) {
		return;
	}
	const snap = {
		// checkboxes / radios
		checks: Array.from(panel.querySelectorAll('input[type="checkbox"], input[type="radio"]'))
			.map((i) => ({ name: i.name, value: i.value, checked: i.checked })),
		// common scalar fields
		fields: ['from', 'to', 'max'].map((n) => {
			const el = panel.querySelector(`[name="${ n }"]`);
			return { name: n, value: el ? el.value : '' };
		})
	};
	panel.__snap = snap;
}

function restorePanel(panel) {
	const s = panel?.__snap; if (!s) {
		return;
	}
	s.checks.forEach(({ name, value, checked }) => {
		panel.querySelectorAll(`input[name="${ CSS.escape(name) }"]`).forEach((i) => {
			if (i.type === 'radio') {
				i.checked = (i.value === value) && checked;
			} else if (i.type === 'checkbox') {
				if (i.value === value) {
					i.checked = checked;
				}
			}
		});
	});
	s.fields.forEach(({ name, value }) => {
		const el = panel.querySelector(`[name="${ CSS.escape(name) }"]`);
		if (el) {
			el.value = value;
		}
	});
}

export default function showFilters() {
	const bar = document.querySelector('[data-filterbar]');
	if (!bar) {
		return;
	}

	const form = bar.closest('form') || document.querySelector('form');
	const list = document.querySelector('[data-results]');
	const endpoint = bar.getAttribute('data-endpoint') || '/wp-json/olt/v1/shows';
	const forceToday = bar.getAttribute('data-force-today') === '1';
	const forceOffers = bar.getAttribute('data-force-offers') === '1';
	const listBase = (bar.getAttribute('data-list-base') || '').trim();

	const defaultGenreCsv = (bar.getAttribute('data-default-genre') || '').trim();
	const genreLocked = bar.getAttribute('data-genre-locked') === '1';
	const defaultGenres = defaultGenreCsv ? defaultGenreCsv.split(',').map((s) => s.trim()).filter(Boolean) : [];
	const defaultSort = (bar.getAttribute('data-default-sort') || 'name_asc').trim();


	const forcedIgnoreKeys = ['list_base'];
	if (forceOffers) {
		forcedIgnoreKeys.push('offers');
	}
	if (forceToday) {
		forcedIgnoreKeys.push('today');
	}

	const MULTI = new Set(['genre', 'time']);
	const DEFAULTS = {
		q: '', from: '', to: '', time: '',
		max: '',
		today: '', offers: '', advance_offers: '', 'todays-offers': '',
		coming_soon: '', last_chance: '',
		window: '365', sort: 'name_asc',
		list_base: listBase
	};
	const SORT_MAP = {
		'name-atz': 'name_asc', 'name-zta': 'name_desc',
		'date-old': 'date_old', 'date-new': 'date_new',
		'price-low': 'price_low', 'price-high': 'price_high'
	};
	const normaliseSort = (v) => SORT_MAP[ v ] || v || 'name_asc';

	/*
	 * Toggle filters that live inside the All Filters modal and are not covered
	 * by the standard checkbox/price/date count logic. Passed to every
	 * updateAllFiltersCount call so the button count and active state stay in
	 * sync with these filters.
	 */
	const modalToggleKeys = forceOffers ? ['today', 'advance_offers'] : ['coming_soon', 'last_chance'];

	// Wire up search input cleaning
	const qInput = form.querySelector('input[name="q"]');
	if (qInput) {
		// Clean on initial load
		if (qInput.value) {
			qInput.value = cleanBackslashes(qInput.value);
		}
		// Clean on typing (before the debounced search runs)
		qInput.addEventListener('input', () => {
			const cleaned = cleanBackslashes(qInput.value);
			if (qInput.value !== cleaned) {
				qInput.value = cleaned;
			}
		});
	}

	const setTriggerActive = (key, active) => {
		const btn = bar.querySelector(`[data-open="${ key }"]`);
		if (!btn) {
			return;
		}
		btn.classList.toggle('is-active', !!active);
		btn.setAttribute('aria-pressed', active ? 'true' : 'false');
	};
	const isPriceActive = () => {
		// Single-thumb max-only slider; min axis removed across all filter bars.
		const max = form.querySelector('input[name="max"]')?.value ?? '';
		return max !== '' && Number(max) < 100;
	};
	const isTimeActive = () => !!form.querySelector('input[name="time[]"]:checked');
	const isGenreActive = () => {
		const checked = Array.from(form.querySelectorAll('input[name="genre[]"]:checked')).map((i) => i.value);
		if (!genreLocked) {
			return checked.length > 0;
		}
		const L = new Set(defaultGenres);
		if (checked.length !== L.size) {
			return checked.length > 0;
		}
		for (const v of checked) {
			if (!L.has(v)) {
				return true;
			}
		}
		return false;
	};
	const setAllTriggersFromForm = () => {
		setTriggerActive('price', isPriceActive());
		setTriggerActive('time', isTimeActive());
		setTriggerActive('genre', isGenreActive());
		const hasDate = !!(form.querySelector('input[name="from"]')?.value || form.querySelector('input[name="to"]')?.value);
		setTriggerActive('date', hasDate);
	};

	const paramsFromForm = () => {
		const obj = getInlineObj(form, MULTI);
		if (Array.isArray(obj.genre)) {
			obj.genre = [...new Set(obj.genre.map((value) => String(value).trim()).filter(Boolean))].sort();
		}
		if (Array.isArray(obj.time)) {
			obj.time = obj.time.filter(Boolean);
		}
		if (forceToday) {
			obj.today = '1';
		}
		if (forceOffers) {
			obj.offers = '1';
		}

		if (forceOffers && !obj.advance_offers) {
			obj.offers = '1';
		}

		if (genreLocked) {
			obj.genre = defaultGenres.slice();
		}

		// If only 'from' is set (single date), auto-set 'to' to the same date
		if (obj.from && !obj.to) {
			obj.to = obj.from;
		}

		obj.sort = obj.sort || defaultSort;

		// Add list_base for analytics tracking
		if (listBase) {
			obj.list_base = listBase;
		}

		return obj;
	};

	// Analytics: distinguish the pages this bar serves so filter events are
	// attributable to the listing the user was actually on.
	const analyticsContext = forceOffers ? 'special_offers' : (forceToday ? 'todays_offers' : 'shows');
	const filterTracker = createFilterTracker(analyticsContext);

	const render = initFilterBar({
		bar, form, list, endpoint,
		defaults: DEFAULTS, multi: MULTI, normaliseSort, paramsFromForm,
		toggleKeys: modalToggleKeys,
		onBeforeOpenScope(key, { modal, panel }) {
			if (panel) {
				const pKey = panel.getAttribute('data-panel') || '';
				let activeAtOpen = false;

				if (pKey === 'price') {
					const max = form.querySelector('input[name="max"]')?.value ?? '';
					activeAtOpen = max !== '' && Number(max) < 100;
				} else if (pKey === 'time') {
					activeAtOpen = !!form.querySelector('input[name="time[]"]:checked');
				} else if (pKey === 'genre') {
					const checked = Array.from(form.querySelectorAll('input[name="genre[]"]:checked')).map((i) => i.value);
					if (genreLocked) {
						const L = new Set(defaultGenres || []);
						activeAtOpen = (checked.length !== L.size) || checked.some((v) => !L.has(v));
					} else {
						activeAtOpen = checked.length > 0;
					}
				} else if (pKey === 'date') {
					const f = form.querySelector('input[name="from"]')?.value || '';
					const t = form.querySelector('input[name="to"]')?.value || '';
					activeAtOpen = !!(f || t);
				}

				panel.setAttribute('data-active', activeAtOpen ? '1' : '0');
				snapshotPanel(panel);
				adjustPopoverPosition(panel);
			}

			if (key === 'date' && panel) {
				const container = panel.querySelector('[data-range-calendar],[data-range-calendar="popover"]');
				const from = form.querySelector('input[name="from"]');
				const to = form.querySelector('input[name="to"]');
				if (container && !container.dataset.calBound && from && to) {
					wireCalendarEnable(panel, container, from, to, () => {
						mountFarloRangeCalendar(container, from, to, 1);
					});
				}
			}
			if (key === 'all' && modal) {
				const container = modal.querySelector('[data-range-calendar="modal"]');
				const mFrom = modal.querySelector('[data-modal-from]');
				const mTo = modal.querySelector('[data-modal-to]');
				const inlineFrom = form.querySelector('input[name="from"]')?.value || '';
				const inlineTo = form.querySelector('input[name="to"]')?.value || '';
				if (mFrom) {
					mFrom.value = inlineFrom;
				}
				if (mTo) {
					mTo.value = inlineTo;
				}
				if (container && !container.dataset.calBound && mFrom && mTo) {
					wireCalendarEnable(modal, container, mFrom, mTo, () => {
						const mq = matchMedia('(min-width:768px)');
						const mount = () => {
							container.innerHTML = '';
							mountFarloRangeCalendar(container, mFrom, mTo, mq.matches ? 2 : 1);
						};
						mount();
						(mq.addEventListener ? mq.addEventListener('change', mount) : mq.addListener(mount));
					});
				}
				const timeChecked = Array.from(form.querySelectorAll('input[name="time[]"]:checked')).map((i) => i.value);
				modal.querySelectorAll('[data-modal-time]').forEach((c) => {
					c.checked = timeChecked.includes(c.value);
				});
				const genresChecked = Array.from(form.querySelectorAll('input[name="genre[]"]:checked')).map((i) => i.value);
				modal.querySelectorAll('[data-modal-genre]').forEach((c) => {
					c.checked = genresChecked.includes(c.value);
				});

				if (genreLocked) {
					modal.querySelectorAll('[data-modal-genre]').forEach((c) => {
						c.checked = defaultGenres.includes(c.value);
						c.disabled = true;
					});
				}

				// today/coming_soon/last_chance
				['coming_soon', 'last_chance', 'today', 'advance_offers'].forEach((key) => {
					const active = form.querySelector(`input[name="${ key }"]`)?.value === '1';
					modal.querySelectorAll(`[data-modal-toggle="${ key }"]`).forEach((btn) => {
						btn.classList.toggle('is-active', active);
						btn.setAttribute('aria-pressed', active ? 'true' : 'false');
					});
				});
			}
		},
		onAfterRender(data, params) {
			// Emit dataLayer events for any filters that changed this render.
			filterTracker.track(params);
			// hydrate infinite scroll
			document.querySelectorAll('[data-listing-hydrate], [data-sentinel]').forEach((n) => n.remove());
			const total = Number(data?.total ?? 0);
			const limit = Number(data?.limit ?? 0);
			const offset = Number(data?.offset ?? 0);
			if (total && limit && offset + limit < total) {
				const wrapper = document.querySelector('.shows-grid');
				if (wrapper && list) {
					const hook = document.createElement('div');
					hook.setAttribute('data-listing-hydrate', '');
					hook.setAttribute('data-endpoint', endpoint);
					hook.setAttribute('data-query', JSON.stringify(params));
					hook.setAttribute('data-offset', String(offset + limit));
					hook.setAttribute('data-chunk', String(limit));
					hook.setAttribute('data-total', String(total));
					hook.setAttribute('data-mode', 'scroll');
					const sentinel = document.createElement('div');
					sentinel.setAttribute('data-sentinel', '');
					sentinel.style.height = '1px';
					wrapper.append(hook, sentinel);
					hydrateTicketListing();
				}
			}
			updateAllFiltersCount(bar, form, {
				multiCsvKeys: new Set(['time', 'genre']),
				toggleKeys: modalToggleKeys,
				ignoreKeys: genreLocked ? ['genre'] : [],
				defaultsMulti: genreLocked ? { genre: defaultGenres } : {}
			});
			updateClearChip(
				bar,
				params,
				DEFAULTS,
				genreLocked
					? { ignoreKeys: ['genre', ...forcedIgnoreKeys], defaultsMulti: { genre: defaultGenres }, preserve: { genre: defaultGenres } }
					: { ignoreKeys: [...forcedIgnoreKeys] }
			);
			applyOfferDateVisibility(list, params?.from || '', params?.to || '');
			setAllTriggersFromForm();
			paintChoiceActives(document);
		}
	});

	// Modal APPLY - copy modal state to inline form inputs, or navigate when a
	// data-modal-goto button has been selected. Navigation takes precedence: if
	// any goto button carries is-active, the user is redirected immediately and
	// no filter state is written back to the form.
	bar.addEventListener('click', (e) => {
		const btn = e.target.closest('[data-submit]'); if (!btn) {
			return;
		}
		const modalEl = btn.closest('dialog[data-modal="all"]'); if (!modalEl) {
			return;
		}

		// Check all data-modal-goto buttons in the modal, not only the first.
		// resolveModalGoto returns the URL of the first active one, or null.
		const gotoUrl = resolveModalGoto(modalEl);
		if (gotoUrl) {
			e.stopImmediatePropagation();
			modalEl.close?.();
			if (list && !list.hasAttribute('tabindex')) {
				list.setAttribute('tabindex', '-1');
			}
			list?.focus?.();
			window.location.href = gotoUrl;
			return;
		}

		// dates
		const mFrom = modalEl.querySelector('[data-modal-from]');
		const mTo = modalEl.querySelector('[data-modal-to]');
		const inFrom = form.querySelector('input[name="from"]') || (() => {
			const i = document.createElement('input'); i.type = 'hidden'; i.name = 'from'; form.appendChild(i); return i;
		})();
		const inTo = form.querySelector('input[name="to"]') || (() => {
			const i = document.createElement('input'); i.type = 'hidden'; i.name = 'to'; form.appendChild(i); return i;
		})();
		inFrom.value = mFrom?.value || '';
		inTo.value = mTo?.value || '';

		// time
		const selectedTimes = Array.from(modalEl.querySelectorAll('[data-modal-time]:checked')).map((i) => i.value);
		const inlineTimes = new Set(selectedTimes);
		form.querySelectorAll('input[name="time[]"]').forEach((i) => {
			i.checked = inlineTimes.has(i.value);
		});

		// genre
		const selectedGenres = new Set(Array.from(modalEl.querySelectorAll('[data-modal-genre]:checked')).map((i) => i.value));
		form.querySelectorAll('input[name="genre[]"]').forEach((i) => {
			i.checked = selectedGenres.has(i.value);
		});

		// price — single-thumb max-only slider; min axis removed.
		const wrap = modalEl.querySelector('[data-modal-price][data-price-wrap]');
		if (wrap) {
			const mx = wrap.querySelector('input[name="max"]');
			let inlineMax = form.querySelector('input[name="max"]'); if (!inlineMax) {
				inlineMax = document.createElement('input'); inlineMax.type = 'hidden'; inlineMax.name = 'max'; form.appendChild(inlineMax);
			}
			inlineMax.value = String(mx?.value ?? '');
		}

		// toggles
		modalEl.querySelectorAll('[data-modal-toggle]').forEach((t) => {
			const name = t.getAttribute('data-modal-toggle');
			let i = form.querySelector(`input[name="${ name }"]`);
			if (!i) {
				i = document.createElement('input'); i.type = 'hidden'; i.name = name; form.appendChild(i);
			}
			i.value = t.classList.contains('is-active') ? '1' : '';
		});

		e.stopImmediatePropagation();
		paintChoiceActives(document);
		modalEl.close?.();
		render();
	}, { capture: true });

	// data-modal-toggle buttons inside the All Filters modal: toggle is-active and
	// mark the dialog dirty so Apply is enabled. data-modal-goto toggling is
	// handled centrally in initFilterBar (filtersCore.js) and must not be
	// duplicated here, as two listeners on the same element would cancel each other.
	bar.addEventListener('click', (e) => {
		const btn = e.target.closest('[data-modal-toggle]'); if (!btn) {
			return;
		}
		e.preventDefault();
		const scope = btn.closest('dialog, .popover__panel') || bar;
		const key = btn.getAttribute('data-modal-toggle');
		const willBeActive = !btn.classList.contains('is-active');

		// Make coming_soon and last_chance mutually exclusive in modal.
		if (key && (key === 'coming_soon' || key === 'last_chance') && willBeActive) {
			const otherKey = key === 'coming_soon' ? 'last_chance' : 'coming_soon';
			const otherBtn = scope.querySelector(`[data-modal-toggle="${ otherKey }"]`);

			if (otherBtn) {
				otherBtn.classList.remove('is-active');
				otherBtn.setAttribute('aria-pressed', 'false');
			}

			// Also deactivate any goto navigation buttons (e.g. Today's Tickets)
			scope.querySelectorAll('[data-modal-goto]').forEach((b) => {
				b.classList.remove('is-active');
				b.setAttribute('aria-pressed', 'false');
			});
		}

		// Make today and advance_offers mutually exclusive in modal.
		if (key && (key === 'today' || key === 'advance_offers') && willBeActive) {
			const otherKey = key === 'today' ? 'advance_offers' : 'today';
			const otherBtn = scope.querySelector(`[data-modal-toggle="${ otherKey }"]`);

			if (otherBtn) {
				otherBtn.classList.remove('is-active');
				otherBtn.setAttribute('aria-pressed', 'false');
			}
		}

		// Make today and coming_soon mutually exclusive in modal.
		if (key && (key === 'today' || key === 'coming_soon') && willBeActive) {
			const otherKey = key === 'today' ? 'coming_soon' : 'today';
			const otherBtn = scope.querySelector(`[data-modal-toggle="${ otherKey }"]`);

			if (otherBtn) {
				otherBtn.classList.remove('is-active');
				otherBtn.setAttribute('aria-pressed', 'false');
			}
		}

		btn.classList.toggle('is-active', willBeActive);
		btn.setAttribute('aria-pressed', willBeActive ? 'true' : 'false');
		if (!willBeActive) {
			btn.blur();
		}
		if (scope) {
			markScopeDirty(scope, true);
		}
	});

	// header chips (coming soon / last chance / offers)
	bar.addEventListener('click', (e) => {
		const chip = e.target.closest('[data-filter-toggle]'); if (!chip) {
			return;
		}
		const key = chip.getAttribute('data-filter-toggle');
		if (key === 'offers' && forceOffers) {
			return;
		}
		let i = form.querySelector(`input[name="${ key }"]`);
		if (!i) {
			i = document.createElement('input'); i.type = 'hidden'; i.name = key; form.appendChild(i);
		}
		const was = chip.classList.contains('is-active');

		// Make coming_soon and last_chance mutually exclusive
		if ((key === 'coming_soon' || key === 'last_chance') && !was) {
			const otherKey = key === 'coming_soon' ? 'last_chance' : 'coming_soon';
			const otherInput = form.querySelector(`input[name="${ otherKey }"]`);
			const otherChip = bar.querySelector(`[data-filter-toggle="${ otherKey }"]`);

			if (otherInput) {
				otherInput.value = '';
			}
			if (otherChip) {
				otherChip.classList.remove('is-active');
				otherChip.setAttribute('aria-pressed', 'false');
			}
		}

		if ((key === 'today' || key === 'coming_soon') && !was) {
			const otherKey = key === 'today' ? 'coming_soon' : 'today';
			const otherInput = form.querySelector(`input[name="${ otherKey }"]`);
			const otherChip = bar.querySelector(`[data-filter-toggle="${ otherKey }"]`);

			if (otherInput) {
				otherInput.value = '';
			}
			if (otherChip) {
				otherChip.classList.remove('is-active');
				otherChip.setAttribute('aria-pressed', 'false');
			}
		}

		if ((key === 'today' || key === 'advance_offers') && !was) {
			const otherKey = key === 'today' ? 'advance_offers' : 'today';
			const otherInput = form.querySelector(`input[name="${ otherKey }"]`);
			const otherChip = bar.querySelector(`[data-filter-toggle="${ otherKey }"]`);

			if (otherInput) {
				otherInput.value = '';
			}
			if (otherChip) {
				otherChip.classList.remove('is-active');
				otherChip.setAttribute('aria-pressed', 'false');
			}

			// When switching to Today's Offers, clear any date range
			if (key === 'today') {
				const fromInput = form.querySelector('input[name="from"]');
				const toInput = form.querySelector('input[name="to"]');
				if (fromInput) fromInput.value = '';
				if (toInput) toInput.value = '';
				setTriggerActive('date', false);
			}
		}

		i.value = was ? '' : '1';
		chip.classList.toggle('is-active', !was);
		if (was) {
			chip.blur();
		}
		render();
	});

	// popover Apply/Clear (date|time|price|genre)
	bar.addEventListener('click', (e) => {
		const panel = e.target.closest('.popover__panel'); if (!panel) {
			return;
		}
		const applyBtn = e.target.closest('[data-submit]');
		const clearBtn = e.target.closest('[data-cancel]');
		if (!applyBtn && !clearBtn) {
			return;
		}

		const key = panel.getAttribute('data-panel') || '';

		if (clearBtn) {
			if (genreLocked && key === 'genre') {
				restorePanel(panel);
				panel.setAttribute('hidden', '');
				return;
			}
			const wasActiveAtOpen = panel.getAttribute('data-active') === '1';

			if (!wasActiveAtOpen) {
				// Nothing applied previously: Clear should behave like Cancel.
				// Restore any tweaks the user made in the popover and close.
				restorePanel(panel);
				panel.setAttribute('hidden', '');
				return;
			}

			// Filter WAS active previously: actually clear it and apply.
			if (key === 'price') {
				panel.querySelectorAll('input[name="max"]').forEach((i) => i.value = '');
				panel.querySelector('[data-price-wrap]')?.resetPriceUI?.();
				let inlineMax = form.querySelector('input[name="max"]'); if (!inlineMax) {
					inlineMax = document.createElement('input'); inlineMax.type = 'hidden'; inlineMax.name = 'max'; form.appendChild(inlineMax);
				}
				inlineMax.value = '';
			} else if (key === 'time') {
				panel.querySelectorAll('input[name="time[]"]').forEach((i) => i.checked = false);
			} else if (key === 'genre') {
				panel.querySelectorAll('input[name="genre[]"]').forEach((i) => i.checked = false);
			} else if (key === 'date') {
				const inFrom = form.querySelector('input[name="from"]') || (() => {
					const i = document.createElement('input'); i.type = 'hidden'; i.name = 'from'; form.appendChild(i); return i;
				})();
				const inTo = form.querySelector('input[name="to"]') || (() => {
					const i = document.createElement('input'); i.type = 'hidden'; i.name = 'to'; form.appendChild(i); return i;
				})();
				inFrom.value = ''; inTo.value = '';
				panel.querySelector('[data-popover-from]')?.setAttribute('value', '');
				panel.querySelector('[data-popover-to]')?.setAttribute('value', '');
			}

			setAllTriggersFromForm();
			paintChoiceActives(document);

			const paramsNow = paramsFromForm();
			updateAllFiltersCount(bar, form, {
				multiCsvKeys: new Set(['time', 'genre']),
				toggleKeys: modalToggleKeys,
				ignoreKeys: genreLocked ? ['genre'] : [],
				defaultsMulti: genreLocked ? { genre: defaultGenres } : {}
			});
			updateClearChip(
				bar,
				paramsNow,
				DEFAULTS,
				genreLocked
					? { ignoreKeys: ['genre', ...forcedIgnoreKeys], defaultsMulti: { genre: defaultGenres }, preserve: { genre: defaultGenres } }
					: { ignoreKeys: [...forcedIgnoreKeys] }
			);

			clearBtn.blur?.();
			panel.setAttribute('hidden', '');
			render();
			return;
		}

		if (applyBtn) {
			if (key === 'price') {
				const wrap = panel.querySelector('[data-price-wrap]');
				if (wrap) {
					const srcMax = wrap.querySelector('input[name="max"]');
					let inlineMax = form.querySelector('input[name="max"]'); if (!inlineMax) {
						inlineMax = document.createElement('input'); inlineMax.type = 'hidden'; inlineMax.name = 'max'; form.appendChild(inlineMax);
					}
					inlineMax.value = String(srcMax?.value ?? '');
				}
			}
			// On offers page, auto-activate Advance Offers when a future date range is applied
			if (forceOffers && key === 'date') {
				const fromVal = form.querySelector('input[name="from"]')?.value || '';
				const todayStr = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD
				const advInput = form.querySelector('input[name="advance_offers"]') || (() => {
					const el = document.createElement('input'); el.type = 'hidden'; el.name = 'advance_offers'; form.appendChild(el); return el;
				})();
				const advChip = bar.querySelector('[data-filter-toggle="advance_offers"]');
				const todayInput = form.querySelector('input[name="today"]');
				const todayChip = bar.querySelector('[data-filter-toggle="today"]');

				if (fromVal && fromVal > todayStr) {
					advInput.value = '1';
					if (advChip) { advChip.classList.add('is-active'); advChip.setAttribute('aria-pressed', 'true'); }
					if (todayInput) todayInput.value = '';
					if (todayChip) { todayChip.classList.remove('is-active'); todayChip.setAttribute('aria-pressed', 'false'); }
				} else {
					advInput.value = '';
					if (advChip) { advChip.classList.remove('is-active'); advChip.setAttribute('aria-pressed', 'false'); }
				}
			}

			setAllTriggersFromForm();
			paintChoiceActives(document);

			const paramsNow = paramsFromForm();
			updateAllFiltersCount(bar, form, {
				multiCsvKeys: new Set(['time', 'genre']),
				toggleKeys: modalToggleKeys,
				ignoreKeys: genreLocked ? ['genre'] : [],
				defaultsMulti: genreLocked ? { genre: defaultGenres } : {}
			});
			updateClearChip(
				bar,
				paramsNow,
				DEFAULTS,
				genreLocked
					? { ignoreKeys: ['genre', ...forcedIgnoreKeys], defaultsMulti: { genre: defaultGenres }, preserve: { genre: defaultGenres } }
					: { ignoreKeys: [...forcedIgnoreKeys] }
			);

			applyBtn.blur?.();
			panel.setAttribute('hidden', '');
			render();
		}
	});

	// promote hidden default genres to checkboxes once
	{
		const url = new URL(window.location.href);
		const urlHasGenre = url.searchParams.has('genre');
		if (!urlHasGenre) {
			const hiddenDefaults = new Set(
				Array.from(form.querySelectorAll('input[name="genre[]"][type="hidden"]')).map((i) => i.value).filter(Boolean)
			);
			if (hiddenDefaults.size) {
				form.querySelectorAll('input[name="genre[]"][type="checkbox"]').forEach((cb) => {
					cb.checked = hiddenDefaults.has(cb.value);
				});
				form.querySelectorAll('input[name="genre[]"][type="hidden"]').forEach((n) => n.remove());
			}
		}
	}

	paintChoiceActives(document);
	setAllTriggersFromForm();

	updateClearChip(
		bar,
		paramsFromForm(),
		DEFAULTS,
		genreLocked
			? { ignoreKeys: ['genre', ...forcedIgnoreKeys], defaultsMulti: { genre: defaultGenres }, preserve: { genre: defaultGenres } }
			: { ignoreKeys: [...forcedIgnoreKeys] }
	);

	// Capture the fully-seeded initial filter state as the analytics baseline.
	// Runs after URL seeding and the genre-default promotion above so pre-applied
	// filters are not mis-reported as fresh user selections. This must precede any
	// render() below (whose onAfterRender diffs against this baseline).
	filterTracker.init(paramsFromForm());

	// When a shows filter is active the SSR content is unfiltered (the server-side
	// render does not apply the shows param), so a JS render must always fire.
	const hasActiveShowsFilter = paramsFromForm().shows?.length > 0;
	const hasSSR = !hasActiveShowsFilter && list?.dataset?.ssrInitial === '1' && (list.firstElementChild || (list.textContent || '').trim().length > 0);
	if (!hasSSR) {
		const params = paramsFromForm();
		params.sort = params.sort || defaultSort;
		params.window = params.window || '365';
		updateAllFiltersCount(bar, form, {
			multiCsvKeys: new Set(['time', 'genre']),
			toggleKeys: modalToggleKeys,
			ignoreKeys: genreLocked ? ['genre'] : [],
			defaultsMulti: genreLocked ? { genre: defaultGenres } : {}
		});
		updateClearChip(
			bar,
			params,
			DEFAULTS,
			genreLocked
				? { ignoreKeys: ['genre', ...forcedIgnoreKeys], defaultsMulti: { genre: defaultGenres }, preserve: { genre: defaultGenres } }
				: { ignoreKeys: [...forcedIgnoreKeys] }
		);
		render();
	} else {
		const from = form.querySelector('input[name="from"]')?.value || '';
		const to = form.querySelector('input[name="to"]')?.value || '';
		applyOfferDateVisibility(list, from, to);
	}
}
